/**
 * lastModule — remembers the last module instance a learner successfully loaded,
 * plus their scroll depth in it, so we can offer an exact "resume where you left off"
 * after a reload or after navigating back to the pathway page.
 *
 * Stored per user in localStorage; purged on sign-out / account switch.
 */

const PREFIX = "gglife.lastmodule.";

export type LastModuleEntry = {
  slug: string;
  moduleId: string;
  title?: string | null;
  /** 0-100 scroll depth within the module reader. */
  scrollPct: number;
  savedAt: number;
};

const keyFor = (userId: string | null) => `${PREFIX}${userId ?? "guest"}`;

export function readLastModule(userId: string | null): LastModuleEntry | null {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastModuleEntry;
    if (!parsed || typeof parsed.slug !== "string" || typeof parsed.moduleId !== "string") {
      return null;
    }
    return {
      slug: parsed.slug,
      moduleId: parsed.moduleId,
      title: parsed.title ?? null,
      scrollPct: Number.isFinite(parsed.scrollPct) ? Number(parsed.scrollPct) : 0,
      savedAt: Number.isFinite(parsed.savedAt) ? Number(parsed.savedAt) : 0,
    };
  } catch {
    return null;
  }
}

export function writeLastModule(
  userId: string | null,
  entry: Omit<LastModuleEntry, "savedAt"> & { savedAt?: number },
) {
  try {
    const payload: LastModuleEntry = {
      slug: entry.slug,
      moduleId: entry.moduleId,
      title: entry.title ?? null,
      scrollPct: Math.min(100, Math.max(0, Math.round(entry.scrollPct || 0))),
      savedAt: entry.savedAt ?? Date.now(),
    };
    window.localStorage.setItem(keyFor(userId), JSON.stringify(payload));
  } catch {
    /* ignore quota / privacy-mode failures */
  }
}

/** Updates only the scroll depth if the stored entry matches this module. */
export function updateLastModuleScroll(
  userId: string | null,
  moduleId: string,
  scrollPct: number,
) {
  const current = readLastModule(userId);
  if (!current || current.moduleId !== moduleId) return;
  writeLastModule(userId, { ...current, scrollPct });
}

export function clearLastModule(userId: string | null) {
  try {
    window.localStorage.removeItem(keyFor(userId));
  } catch {
    /* ignore */
  }
}

/** Removes every stored pointer except the one belonging to `keepUserId` (if any). */
export function clearAllLastModules(keepUserId?: string | null) {
  try {
    const keep = keepUserId ? keyFor(keepUserId) : null;
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX) && k !== keep) doomed.push(k);
    }
    doomed.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
